import React from 'react';
import { Link } from 'react-router-dom';
import {
  Linkedin,
  Mail,
  GraduationCap,
  Briefcase,
  ArrowRight,
} from 'lucide-react';
import { Logo } from '../components/Logo';
import { PublicHeader } from '../components/PublicHeader';
import { useForceLightMode } from '../hooks/useForceLightMode';

const founders = [
  {
    name: 'Dr Khaled Abdel-Aziz PhD FRCP',
    role: 'Medical Co-founder',
    bio: 'Khaled qualified as a medical doctor in 2004 and has over 22 years of experience working within the NHS. He has practised as a Consultant Neurologist at St Peter\'s Hospital, Chertsey and St George\'s Hospital, London since 2015 and has worked as an expert witness for nearly 10 years.\n\nKhaled has acted as an expert witness in over 400 civil claims of clinical negligence and personal injury and receives instructions from both Claimant and Defendant solicitors. His frontline clinical experience and understanding of medico-legal workflows drives how ClinLexis is meeting the needs of medico-legal professionals.',
    credentials: [
      { icon: GraduationCap, text: 'Fellow — Royal College of Physicians, London' },
      { icon: GraduationCap, text: 'PhD Neuroscience — University College London' },
      { icon: GraduationCap, text: 'MBChB — University of Leeds' },
      { icon: GraduationCap, text: 'BSc Neuroscience — University of Leeds' },
      { icon: Briefcase, text: 'Consultant Neurologist — NHS (11+ years)' },
      { icon: Briefcase, text: 'Certified Expert Witness — Cardiff University Bond Solon' },
      { icon: Briefcase, text: 'Background of applied artificial intelligence research within epilepsy and headache medicine' },
    ],
    email: 'khaled@clinlexis.co.uk',
    linkedin: 'https://www.linkedin.com/in/khaled-abdel-aziz-45062b6a/',
  },
  {
    name: 'Ahmmad Youssef, PhD',
    role: 'Technical Co-founder',
    bio: 'Ahmmad has over two decades of experience building secure, large-scale systems in highly regulated environments — with AI and engineering technology leadership roles at JPMorgan Chase, Deutsche Bank, and Barclays. His PhD research at Brunel University focused on large-scale data analytics and software quality — the same principles that underpin how ClinLexis processes and surfaces insights from complex medical records.\n\nHe brings deep expertise in cloud architecture, AI, and building platforms where security and compliance are non-negotiable. Ahmmad has also advised many FTSE companies on how to use Generative AI to transform their businesses.',
    credentials: [
      { icon: GraduationCap, text: 'BSc & MSc Computer Science — Queen Mary University of London' },
      { icon: GraduationCap, text: 'PhD in Data Analytics & Software Quality — Brunel University' },
      { icon: Briefcase, text: 'AI & Engineering Technology Leadership at JPMorgan Chase, Deutsche Bank, and Barclays' },
    ],
    email: 'ahmmad@clinlexis.co.uk',
    linkedin: 'https://www.linkedin.com/in/ahmmad-youssef/',
  },
];

type TeamMember = {
  name: string;
  role: string;
  bio: string;
  credentials?: { icon: typeof GraduationCap; text: string }[];
  linkedin?: string;
};

const team: TeamMember[] = [
  {
    name: 'Alex Brown',
    role: 'Sales Lead',
    bio: 'Alex leads sales at ClinLexis. He runs product demos for solicitors and expert witnesses and owns customer onboarding, guiding new firms and experts from their first demo through to active use of the platform. A fourth-year medical student at St George\'s, University of London, he combines clinical training with hands-on experience in healthcare sales, having previously worked as a Surgical Solutions Manager at Smith+Nephew.\n\nA retired professional rugby player who represented Saracens, Exeter Chiefs, USAP and Bath Rugby, Alex now serves as Head Coach of St George\'s Hospital Medical School RFC.',
    linkedin: 'https://www.linkedin.com/in/alex-brown-84a0b658/',
  },
  {
    name: 'Farouk Ahmed',
    role: 'Operations Lead',
    bio: 'Farouk runs operations at ClinLexis. He owns the day-to-day mechanics of the business, including support workflows, billing and account administration, vendor coordination, and the internal processes that keep a small team moving quickly.\n\nHe is the first point of contact when something needs sorting, working closely with our medical and engineering teams to make sure solicitors and expert witnesses get a responsive, well-run experience once they are on the platform.',
  },
];

export const TeamPage: React.FC = () => {
  useForceLightMode();

  return (
    <div className="min-h-screen bg-gradient-to-br from-medical-50 via-white to-primary-50/30">
      <PublicHeader />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
        {/* Hero */}
        <div className="text-center max-w-3xl mx-auto mb-20">
          <h1 className="text-4xl md:text-5xl font-bold text-medical-900 mb-6 leading-tight tracking-tight">
            Built by clinicians and engineers
          </h1>
          <p className="text-xl text-medical-600 leading-relaxed font-light">
            ClinLexis was co-founded by an NHS consultant neurologist and an AI technology leader
            who saw first-hand how much time medico-legal professionals spend on manual case
            preparation. They built ClinLexis to give UK medico-legal professionals
            a leading-edge AI platform that is secure, responsible, explainable, and guided by clinical oversight.
          </p>
        </div>

        {/* Founders */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 mb-20">
          {founders.map((founder, index) => (
            <div key={index} className="bg-white/60 backdrop-blur-sm rounded-2xl p-8 md:p-10 border border-medical-200/50">
              <div className="mb-6">
                <h2 className="text-2xl font-bold text-medical-900 mb-1">{founder.name}</h2>
                <p className="text-primary-600 font-semibold">{founder.role}</p>
              </div>

              <div className="text-medical-600 leading-relaxed mb-8 text-sm space-y-3">
                {founder.bio.split('\n\n').map((paragraph, i) => (
                  <p key={i}>{paragraph}</p>
                ))}
              </div>

              <div className="space-y-3 mb-8">
                {founder.credentials.map((cred, i) => (
                  <div key={i} className="flex items-start space-x-3">
                    <cred.icon className="h-4 w-4 text-primary-600 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-medical-700">{cred.text}</span>
                  </div>
                ))}
              </div>

              <div className="flex flex-wrap gap-4">
                <a
                  href={`mailto:${founder.email}`}
                  className="inline-flex items-center space-x-2 text-sm font-semibold text-medical-600 hover:text-primary-600 transition-colors"
                >
                  <Mail className="h-4 w-4" />
                  <span>{founder.email}</span>
                </a>
                <a
                  href={founder.linkedin}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center space-x-2 text-sm font-semibold text-medical-600 hover:text-primary-600 transition-colors"
                >
                  <Linkedin className="h-4 w-4" />
                  <span>LinkedIn</span>
                </a>
              </div>
            </div>
          ))}
        </div>

        {/* Team */}
        <div className="mb-20">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <h2 className="text-2xl md:text-3xl font-bold text-medical-900 mb-3 tracking-tight">
              Our team
            </h2>
            <p className="text-medical-600 leading-relaxed">
              Working alongside our founders to bring ClinLexis to medico-legal professionals.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 max-w-5xl mx-auto">
            {team.map((member, index) => (
              <div key={index} className="bg-white/60 backdrop-blur-sm rounded-2xl p-8 md:p-10 border border-medical-200/50">
                <div className="mb-6">
                  <h2 className="text-2xl font-bold text-medical-900 mb-1">{member.name}</h2>
                  <p className="text-primary-600 font-semibold">{member.role}</p>
                </div>

                <div className="text-medical-600 leading-relaxed mb-8 text-sm space-y-3">
                  {member.bio.split('\n\n').map((paragraph, i) => (
                    <p key={i}>{paragraph}</p>
                  ))}
                </div>

                {member.credentials && member.credentials.length > 0 && (
                  <div className="space-y-3 mb-8">
                    {member.credentials.map((cred, i) => (
                      <div key={i} className="flex items-start space-x-3">
                        <cred.icon className="h-4 w-4 text-primary-600 flex-shrink-0 mt-0.5" />
                        <span className="text-sm text-medical-700">{cred.text}</span>
                      </div>
                    ))}
                  </div>
                )}

                {member.linkedin && (
                  <div className="flex flex-wrap gap-4">
                    <a
                      href={member.linkedin}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center space-x-2 text-sm font-semibold text-medical-600 hover:text-primary-600 transition-colors"
                    >
                      <Linkedin className="h-4 w-4" />
                      <span>LinkedIn</span>
                    </a>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Why we built this */}
        <div className="bg-gradient-to-br from-medical-800 to-medical-900 rounded-2xl p-10 md:p-12 text-white mb-20">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-2xl md:text-3xl font-bold mb-4 tracking-tight">
              Why we built ClinLexis
            </h2>
            <p className="text-medical-300 leading-relaxed">
              Medical experts spend hours reading through clinical records, cross-referencing documents,
              and preparing chronologies by hand. Solicitors spend even longer reviewing them.
              We built ClinLexis because we believed this work could be faster, more accurate,
              and more accessible — without compromising on security or clinical integrity.
            </p>
          </div>
        </div>

        {/* Join us CTA */}
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-medical-900 mb-4 tracking-tight">
            Join the team
          </h2>
          <p className="text-medical-600 mb-8 leading-relaxed">
            We're a small team with big ambitions. If you're excited about improving how medico-legal
            professionals work, we'd love to hear from you.
          </p>
          <Link
            to="/jobs"
            className="btn-primary px-8 py-3.5 text-base inline-flex items-center space-x-2 group shadow-lg shadow-primary-200/50 hover:shadow-xl transition-all"
          >
            <span>View open roles</span>
            <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>
      </div>

      {/* Footer */}
      <footer className="border-t border-medical-200 mt-16 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-2">
          <p className="text-sm text-medical-400 text-center">
            &copy; {new Date().getFullYear()} ClinLexis&trade;. All rights reserved.
          </p>
          <p className="text-xs text-medical-500 text-center">
            ClinLexis Ltd &middot; Company No. 17081483 &middot; Registered in England and Wales &middot; 238a Telegraph Road, Heswall, Wirral, CH60 0AL
          </p>
        </div>
      </footer>
    </div>
  );
};
